// useMaintenanceVendorRequests.jsx
// Hook for fetching and managing maintenance vendor requests

import { getDatabase, ref, get, update, remove } from "firebase/database";
import { useAuth } from "./useAuth";

const useMaintenanceVendorRequests = () => {
  const { currentUser } = useAuth();
  const database = getDatabase();

  // Fetch all maintenance vendor requests for user's properties
  const fetchMaintenanceVendorRequests = async () => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      // Step 1: Get the user's listings
      const listingsRef = ref(database, `users/${currentUser.uid}/listings`);
      const listingsSnapshot = await get(listingsRef);

      if (!listingsSnapshot.exists()) {
        return [];
      }

      const listings = listingsSnapshot.val();
      const vendorRequests = [];

      // Step 2: Fetch vendor requests for each listing
      const listingIds = Object.keys(listings);
      for (const listingId of listingIds) {
        const listingDataRef = ref(database, `listings/${listingId}`);
        const listingDataSnapshot = await get(listingDataRef);

        if (!listingDataSnapshot.exists()) {
          continue;
        }

        const listingData = listingDataSnapshot.val();
        const listingAddress = listingData.address || "Address not available";
        const listingCity = listingData.city || "City not available";
        const listingState = listingData.state || "State not available";
        const listingImages = Array.isArray(listingData.images) ? listingData.images : [];
        const listingImage = listingImages.length > 0 ? listingImages[0] : null;

        const vendorRequestsRef = ref(
          database,
          `listings/${listingId}/maintenanceVendorRequests`
        );
        const requestsSnapshot = await get(vendorRequestsRef);

        if (requestsSnapshot.exists()) {
          const requests = Object.entries(requestsSnapshot.val()).map(([id, request]) => ({
            id,
            listingId,
            address: listingAddress,
            city: listingCity,
            state: listingState,
            image: listingImage,
            ...request,
          }));
          vendorRequests.push(...requests);
        }
      }

      return vendorRequests;
    } catch (error) {
      console.error("Error fetching maintenance vendor requests:", error);
      throw error;
    }
  };

  // Approve or deny a maintenance vendor request
  const updateVendorRequestStatus = async (listingId, requestId, status) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      const requestRef = ref(
        database,
        `listings/${listingId}/maintenanceVendorRequests/${requestId}`
      );
      const snapshot = await get(requestRef);

      if (!snapshot.exists()) {
        throw new Error("Vendor request does not exist.");
      }

      await update(requestRef, { 
        status,
        reviewedAt: Date.now(),
        reviewedBy: currentUser.uid,
      });
      console.log(`Vendor request ${requestId} updated to ${status}`);
    } catch (error) {
      console.error("Error updating vendor request status:", error);
      throw error;
    }
  };

  // Generic partial update for one vendor request
  const patchVendorRequest = async (listingId, requestId, updates) => {
    if (!currentUser) throw new Error("User is not authenticated.");
    const requestRef = ref(
      database,
      `listings/${listingId}/maintenanceVendorRequests/${requestId}`
    );
    await update(requestRef, updates);
  };

  // Delete a maintenance vendor request
  const deleteVendorRequest = async (listingId, requestId) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      const requestRef = ref(
        database,
        `listings/${listingId}/maintenanceVendorRequests/${requestId}`
      );
      const snapshot = await get(requestRef);

      if (!snapshot.exists()) {
        throw new Error("Vendor request does not exist.");
      }

      await remove(requestRef);
      console.log(`Vendor request ${requestId} deleted`);
    } catch (error) {
      console.error("Error deleting vendor request:", error);
      throw error;
    }
  };

  return {
    fetchMaintenanceVendorRequests,
    updateVendorRequestStatus,
    patchVendorRequest,
    deleteVendorRequest,
  };
};

export default useMaintenanceVendorRequests;

